import { useEffect } from "react";
import { useIntl } from "react-intl";
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";

import Feedback from "components/Feedback";
import { useGlobalPort } from "components/layouts/DefaultLayout/Topbar/GlobalPortStore";
import { useUserStore } from "components/stores/UserStore";
import AuthorisationDetails from "modules/AuthorisationDetails";
import ViewAsRedirect from "modules/viewAsRedirect";
import { useDocumentTitle } from "utils/hooks/useDocumentTitle";
import { trackFunnelStep, trackPageview } from "utils/plausible";
import { lazy } from "utils/react-lazy-with-reload";

import Providers from "./Providers";

const AdvancedMapPage = lazy(() => import("modules/advancedMap"));
const MapMobileLayout = lazy(
  () => import("modules/advancedMap/mobile/MapMobileLayout"),
);
const MapMobileOutOfServicePage = lazy(
  () => import("modules/advancedMap/mobile/OutOfService"),
);
const MapMobileShorePowerUnitsPage = lazy(
  () => import("modules/advancedMap/mobile/ShorePowerUnits"),
);
const MapMobileSpuDetails = lazy(
  () =>
    import("modules/advancedMap/mobile/ShorePowerUnits/MapMobileSpuDetails"),
);
const MapMobileSupplyPitsPage = lazy(
  () => import("modules/advancedMap/mobile/SupplyPits"),
);
const MapMobileSupplyPitsDetailsPage = lazy(
  () => import("modules/advancedMap/mobile/SupplyPits/Details"),
);
const AlarmCategories = lazy(() => import("modules/alarmCategories"));
const Login = lazy(() => import("modules/auth/Login"));
const PrivacyPolicy = lazy(() => import("modules/auth/Policies"));
const PrivacyPolicyAccept = lazy(
  () => import("modules/auth/Policies/PolicyAccept"),
);
const Auth0VerificationError = lazy(
  () => import("modules/auth0VerificationError"),
);
const Countries = lazy(() => import("modules/countries"));
const CustomersPage = lazy(() => import("modules/customers"));
const CustomerDetails = lazy(() => import("modules/customers/CustomerDetails"));
const DashboardPage = lazy(() => import("modules/dashboard"));
const ChargersPage = lazy(() => import("modules/chargers"));
const AssetsPage = lazy(() => import("modules/assets"));
const PortCallsV2 = lazy(() => import("modules/portCallsV2"));
const ChargerDetailsPage = lazy(
  () => import("modules/chargers/ChargerDetails"),
);

const Invoices = lazy(() => import("modules/invoices"));
const ChargingSessionInvoices = lazy(
  () => import("modules/invoices/ChargingSessionInvoices"),
);
const EditInvoice = lazy(
  () => import("modules/invoices/PortCallInvoices/EditInvoice"),
);
const InvoiceDetails = lazy(
  () => import("modules/invoices/PortCallInvoices/InvoiceDetails"),
);
const InvoiceDetailsSession = lazy(
  () => import("modules/invoices/ChargingSessionInvoices/InvoiceDetails"),
);
const NewInvoice = lazy(
  () => import("modules/invoices/PortCallInvoices/NewInvoice"),
);

const InvoiceServiceProviders = lazy(
  () => import("modules/invoiceServiceProvider"),
);
const OrganisationsPage = lazy(() => import("modules/organisations"));
const OrganisationDetails = lazy(() => import("modules/organisations/Details"));
const MyOrganisationPage = lazy(
  () => import("modules/organisations/my-organisation"),
);
const PortOrganisation = lazy(
  () => import("modules/organisations/PortOrganisation"),
);
const ChargingSessions = lazy(() => import("modules/charging"));
const ChargingSessionDetails = lazy(() => import("modules/charging/Details"));
const PlannedPortCallDetailsPage = lazy(
  () => import("modules/portCallsV2/PlannedPortCallDetails"),
);
const PortCallDetailsPage = lazy(() => import("modules/portcalls/details"));
const PortsPage = lazy(() => import("modules/ports"));
const PortDetails = lazy(() => import("modules/ports/PortDetails"));
const PortConfiguration = lazy(
  () =>
    import("modules/ports/PortTabSections/ShorePowerConfig/PortConfiguration"),
);

const PortFeesPage = lazy(
  () =>
    import("modules/ports/PortTabSections/InvoiceSettings/PortFees/PortFeesPage"),
);
const EnergyReports = lazy(() => import("modules/reports"));
const SPSessionDetailsPage = lazy(
  () => import("modules/SPSessions/SPSessionDetails"),
);
const SPUnitsPage = lazy(() => import("modules/SPUnits"));
const SPUnitDetailsPage = lazy(() => import("modules/SPUnits/SPUnitDetails"));
const SupplyPitsPage = lazy(() => import("modules/supplypits"));
const SupplyPitDetails = lazy(
  () => import("modules/supplypits/supplyPitDetails"),
);
const FaqPage = lazy(() => import("modules/faq"));
const FaqSearchResults = lazy(() => import("modules/faq/SearchResults"));
const InvoicingTutorials = lazy(
  () => import("modules/faq/Tutorials/Invoicing"),
);
const NotifiactionAndAlarmsTutorials = lazy(
  () => import("modules/faq/Tutorials/Todo"),
);
const VesselTutorials = lazy(() => import("modules/faq/Tutorials/Vessels"));
const PortsAndEquipment = lazy(
  () => import("modules/faq/Tutorials/PortsAndEquipment"),
);
const SignalTesting = lazy(() => import("modules/testing/SignalTesting"));
const TodoPage = lazy(() => import("modules/todo"));
const UserNotificationsPage = lazy(
  () => import("modules/user/notifications/UserNotificationsPage"),
);
const UserProfilePage = lazy(
  () => import("modules/user/profile/UserProfilePage"),
);
const Users = lazy(() => import("modules/users"));
const UserDetails = lazy(() => import("modules/users/UserDetails"));
const VesselsPage = lazy(() => import("modules/vessels"));
const MyVesselPage = lazy(() => import("modules/vessels/my-vessel"));
const VesselDetailsPage = lazy(() => import("modules/vessels/VesselDetails"));
const VesselUserInitialInformationGathering = lazy(
  () => import("modules/vessels/VesselUserInitialInformationGathering"),
);

const PrivateRoute = lazy(() => import("components/routes/PrivateRoute"));
const AuthRoute = lazy(() => import("components/routes/AuthRoute"));
const AuthPageWrapper = lazy(() => import("components/routes/AuthPageWrapper"));
const UserRoleBasedRouter = lazy(
  () => import("components/routes/UserRoleBasedRouter"),
);

// Redirect component for shore-power-sessions to sessions with dynamic param
const ShoreSessionRedirect = () => {
  const { shorePowerSessionId } = useParams<{ shorePowerSessionId: string }>();
  return <Navigate to={`/sessions/${shorePowerSessionId}`} replace />;
};

export default function App() {
  useDocumentTitle("Plug Insight");
  return (
    <>
      <Providers>
        <AppRouter />
        <Feedback />
      </Providers>
    </>
  );
}

const AppRouter = () => {
  const location = useLocation();
  const { formatMessage } = useIntl();
  const user = useUserStore((s) => s.user);

  useEffect(() => {
    if (user) {
      trackPageview({
        url: `${window.location.origin}${location.pathname}`,
      });
    }
  }, [user, location]);

  // Funnel tracking: Map / Dashboard / Invoice
  useEffect(() => {
    if (!user) return;
    const path = location.pathname;
    const hash = location.hash?.replace(/^#/, "") ?? "";

    // Invoice funnel: Generate Invoice / Invoiced / Drafts / Non-Billable
    if (path === "/invoices") {
      trackFunnelStep("Dashboard", "Invoice");
      const invoiceStepMap: Record<string, string> = {
        "": "Generate Invoice",
        invoiced: "Invoiced",
        drafts: "Drafts",
        "non-billable": "Non-Billable",
        missing: "Missing",
      };
      const step = invoiceStepMap[hash] ?? "Generate Invoice";
      trackFunnelStep("Invoice", step);
      return;
    }

    // Map funnel: Alarm Event history (route-based steps); Alarm Event history panel is tracked in advancedMap
    if (path.startsWith("/map")) {
      if (path === "/map" || path === "/map/") {
        trackFunnelStep("Map", "Map");
        trackFunnelStep("Dashboard", "Map");
      } else if (path.includes("/out-of-service")) {
        trackFunnelStep("Map", "Out of service");
      } else if (path.includes("/supply-pits")) {
        trackFunnelStep("Map", "Supply Pits");
      } else if (path.includes("/shore-power-units")) {
        trackFunnelStep("Map", "Shore Power Units");
      } else {
        trackFunnelStep("Map", "Map");
      }
      return;
    }

    // Dashboard funnel: Port Calls / Charging / Map / Assets / Invoice / Ports / To Do / Alarm
    const dashboardSteps: { prefix: string; step: string }[] = [
      { prefix: "/dashboard", step: "Dashboard" },
      { prefix: "/port-calls", step: "Port Calls" },
      { prefix: "/charging", step: "Charging" },
      { prefix: "/map", step: "Map" },
      { prefix: "/asset-management", step: "Assets" },
      { prefix: "/invoices", step: "Invoice" },
      { prefix: "/ports", step: "Ports" },
      { prefix: "/todo", step: "To Do" },
      { prefix: "/vessels", step: "Vessels" },
      { prefix: "/alarm-categories", step: "Alarm" },
    ];
    for (const { prefix, step } of dashboardSteps) {
      if (path === prefix || path.startsWith(`${prefix}/`)) {
        trackFunnelStep("Dashboard", step);
        return;
      }
    }
  }, [user, location.pathname, location.hash]);

  const resetGlobalPort = useGlobalPort((s) => s.resetPort);

  useEffect(() => {
    resetGlobalPort();
  }, [resetGlobalPort]);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <AuthRoute>
            <Login />
          </AuthRoute>
        }
      />
      <Route
        path="/user-not-verified"
        element={
          <AuthRoute>
            <Auth0VerificationError />
          </AuthRoute>
        }
      />
      <Route
        path="/policies"
        element={
          <AuthRoute>
            <PrivacyPolicy />
          </AuthRoute>
        }
      />
      <Route
        path="/policies/accept"
        element={
          <AuthRoute>
            <PrivacyPolicyAccept />
          </AuthRoute>
        }
      />

      <Route
        path="/dashboard"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.DASHBOARD" }),
                pathname: "/dashboard",
              },
            ]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
            ]}
          >
            <DashboardPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/chargers"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Chargers" }),
                pathname: "/chargers",
              },
            ]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-standard",
              "portOwner-admin",
            ]}
          >
            <ChargersPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/asset-management"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Assets" }),
                pathname: "/asset-management",
              },
            ]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-standard",
              "portOwner-admin",
              "portControl-standard",
              "portControl-admin",
              "portService-standard",
              "portService-admin",
            ]}
          >
            <AssetsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/chargers/:id"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Chargers details" }),
                pathname: "/chargers/:id",
              },
            ]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-standard",
              "portOwner-admin",
              "portControl-admin",
              "portControl-standard",
              "portService-admin",
              "portService-standard",
            ]}
          >
            <ChargerDetailsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/redirect"
        element={
          <PrivateRoute>
            <ViewAsRedirect />
          </PrivateRoute>
        }
      />

      <Route
        path="/reports"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Reports" }),
                pathname: "/reports",
              },
            ]}
          >
            <EnergyReports />
          </PrivateRoute>
        }
      />

      <Route
        path="/supply-pits"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Supply pits" }),
                pathname: "/supply-pits",
              },
            ]}
            requiredPermissions={["read:supplypits"]}
          >
            <SupplyPitsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/supply-pits/:supplyPitId"
        element={
          <PrivateRoute requiredPermissions={["read:supplypits"]}>
            <SupplyPitDetails />
          </PrivateRoute>
        }
      />

      <Route
        path="/shore-power-units"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Shore power units" }),
                pathname: "/shore-power-units",
              },
            ]}
            requiredPermissions={["read:shorepowerunits"]}
          >
            <SPUnitsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shore-power-units/:shorePowerUnitId"
        element={
          <PrivateRoute requiredPermissions={["read:shorepowerunits"]}>
            <SPUnitDetailsPage />
          </PrivateRoute>
        }
      />
      {/* <Route
        path="/port-calls"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Port calls" }),
                pathname: "/port-calls",
              },
            ]}
            requiredPermissions={["read:portcalls"]}
          >
            <PortCallsPage />
          </PrivateRoute>
        }
      /> */}
      <Route
        path="/port-calls"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Port calls" }),
                pathname: "/port-calls",
              },
            ]}
            requiredPermissions={["read:portcalls"]}
          >
            <PortCallsV2 />
          </PrivateRoute>
        }
      />
      <Route
        path="/charging"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Charging" }),
                pathname: "/charging",
              },
            ]}
          >
            <ChargingSessions />
          </PrivateRoute>
        }
      />
      <Route
        path="/port-calls/charging-session/:id"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Charging sessions" }),
                pathname: "/port-calls/charging-session/:id",
              },
            ]}
            requiredPermissions={["read:portcalls"]}
          >
            <ChargingSessionDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/port-calls/planned/:plannedPortCallId"
        element={
          <PrivateRoute requiredPermissions={["read:portcalls"]}>
            <PlannedPortCallDetailsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/port-calls/:portCallId"
        element={
          <PrivateRoute requiredPermissions={["read:portcalls"]}>
            <PortCallDetailsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/initial-vessel-information"
        element={
          <PrivateRoute
            layout={AuthPageWrapper}
            requiredRole={["vesselOwner-vesseluser"]}
          >
            <VesselUserInitialInformationGathering />
          </PrivateRoute>
        }
      />

      <Route
        path="/sessions/:shorePowerSessionId"
        element={
          <PrivateRoute requiredPermissions={["read:portcalls"]}>
            <SPSessionDetailsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/shore-power-sessions/:shorePowerSessionId"
        element={<ShoreSessionRedirect />}
      />
      <Route
        path="/vessels"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.VESSELS" }),
                pathname: "/vessels",
              },
            ]}
            requiredPermissions={["read:vessels"]}
          >
            <VesselsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/vessels/:vesselId"
        element={
          <PrivateRoute requiredPermissions={["read:vessels"]}>
            <VesselDetailsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-vessel"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.MY_VESSEL" }),
                pathname: "/my-vessel",
              },
            ]}
            requiredPermissions={["read:vessels"]}
            requiredRole={["vesselOwner-vesseluser"]}
          >
            <MyVesselPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing-clients"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Billing clients" }),
                pathname: "/billing-clients",
              },
            ]}
            requiredPermissions={["read:customers"]}
            requiredRole={[
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "system-admin",
              "system-standard",
              "vesselOwner-admin",
              "vesselOwner-standard",
            ]}
          >
            <CustomersPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/billing-clients/:customerId"
        element={
          <PrivateRoute
            requiredPermissions={["read:customers"]}
            requiredRole={[
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "system-admin",
              "system-standard",
              "vesselOwner-admin",
              "vesselOwner-standard",
            ]}
          >
            <CustomerDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/organisations"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.ORGANISATIONS" }),
                pathname: "/organisations",
              },
            ]}
            requiredPermissions={["read:organizations"]}
          >
            <OrganisationsPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoice-service-providers"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({
                  defaultMessage: "Invoice service providers",
                }),
                pathname: "/invoice-service-providers",
              },
            ]}
            requiredPermissions={["read:billing_providers"]}
          >
            <InvoiceServiceProviders />
          </PrivateRoute>
        }
      />
      <Route
        path="/port/my-organisation"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({
                  defaultMessage: "My organisation",
                  description: "Sidebar / My organisation / Menu",
                }),
                pathname: "/port/my-organisation",
              },
            ]}
            requiredPermissions={["read:organizations"]}
          >
            <PortOrganisation />
          </PrivateRoute>
        }
      />
      <Route
        path="/my-organisation"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({
                  defaultMessage: "My organisation",
                  description: "Sidebar / My organisation / Menu",
                }),
                pathname: "/my-organisation",
              },
            ]}
            requiredPermissions={["read:organizations"]}
          >
            <MyOrganisationPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/organisations/:organisationId"
        element={
          <PrivateRoute requiredPermissions={["read:organizations"]}>
            <OrganisationDetails />
          </PrivateRoute>
        }
      />
      <Route
        path="/users"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.USERS" }),
                pathname: "/users",
              },
            ]}
            requiredPermissions={["read:users"]}
          >
            <Users />
          </PrivateRoute>
        }
      />

      <Route
        path="/users/:userId"
        element={
          <PrivateRoute requiredPermissions={["read:users"]}>
            <UserDetails />
          </PrivateRoute>
        }
      />

      <Route
        path="/alarm-categories"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.ALARM_CATEGORIES" }),
                pathname: "/alarm-categories",
              },
            ]}
            requiredPermissions={["read_alarm_categories"]}
          >
            <AlarmCategories />
          </PrivateRoute>
        }
      />
      <Route
        path="/invoices"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.INVOICES" }),
                pathname: "/invoices",
              },
            ]}
            requiredPermissions={["read:invoices"]}
          >
            <Invoices />
          </PrivateRoute>
        }
      />

      <Route
        path="/invoices/charging-sessions"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.INVOICES" }),
                pathname: "/invoices/charging-sessions",
              },
            ]}
            requiredPermissions={["read:invoices"]}
          >
            <ChargingSessionInvoices />
          </PrivateRoute>
        }
      />
      <Route
        path="/ports"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.PORTS" }),
                pathname: "/ports",
              },
            ]}
            requiredPermissions={["read:ports"]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-standard",
              "portControl-admin",
            ]}
          >
            <PortsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/ports/:portid"
        element={
          <PrivateRoute requiredPermissions={["read:ports"]}>
            <PortDetails />
          </PrivateRoute>
        }
      />

      <Route
        path="/countries"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ defaultMessage: "Countries" }),
                pathname: "/countries",
              },
            ]}
            requiredRole={["system-admin", "system-standard"]}
          >
            <Countries />
          </PrivateRoute>
        }
      />

      <Route
        path="/ports/fees/:portid"
        element={
          <PrivateRoute requiredPermissions={["read:ports"]}>
            <PortFeesPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/ports/configuration/:portid"
        element={
          <PrivateRoute requiredPermissions={["read:ports"]}>
            <PortConfiguration />
          </PrivateRoute>
        }
      />

      {/* <Route
        path="/invoices/new"
        element={
          <PrivateRoute
            requiredPermissions={["read:invoices", "write:invoices"]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
            ]} // FIXME: BE adding 'read:invoices' for vesselOwner-*, but we can't yet display it
          >
            <NewInvoicePage />
          </PrivateRoute>
        }
      /> */}

      <Route
        path="/invoices/new"
        element={
          <PrivateRoute
            requiredPermissions={["read:invoices", "write:invoices"]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
            ]} // FIXME: BE adding 'read:invoices' for vesselOwner-*, but we can't yet display it
          >
            <NewInvoice />
          </PrivateRoute>
        }
      />

      <Route
        path="/faq"
        element={
          <PrivateRoute>
            <FaqPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/faq/search-results"
        element={
          <PrivateRoute>
            <FaqSearchResults />
          </PrivateRoute>
        }
      />
      <Route
        path="/faq/tutorials/invoicing"
        element={
          <PrivateRoute>
            <InvoicingTutorials />
          </PrivateRoute>
        }
      />

      <Route
        path="/faq/tutorials/ports-and-equipment"
        element={
          <PrivateRoute>
            <PortsAndEquipment />
          </PrivateRoute>
        }
      />

      <Route
        path="/faq/tutorials/notification-and-alarms"
        element={
          <PrivateRoute>
            <NotifiactionAndAlarmsTutorials />
          </PrivateRoute>
        }
      />

      <Route
        path="/faq/tutorials/vessels"
        element={
          <PrivateRoute>
            <VesselTutorials />
          </PrivateRoute>
        }
      />

      <Route
        path="/invoices/:invoiceId/edit"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.INVOICES" }),
                pathname: "/invoices",
              },
              {
                title: formatMessage({ id: "BREADCRUMBS.INVOICE.EDIT" }),
                pathname: "/invoices/:invoiceId/edit",
              },
            ]}
            requiredPermissions={["read:invoices", "update:invoices"]}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
            ]} // FIXME: BE adding 'read:invoices' for vesselOwner-*, but we can't yet display it
          >
            <EditInvoice />
          </PrivateRoute>
        }
      />

      <Route
        path="/invoices/:invoiceId"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.INVOICES" }),
                pathname: "/invoices",
              },
              {
                title: formatMessage({ id: "BREADCRUMBS.INVOICE.DETAILS" }),
                pathname: "/invoices/:invoiceId",
              },
            ]}
            requiredPermissions={["read:invoices"]}
          >
            <InvoiceDetails />
          </PrivateRoute>
        }
      />

      <Route
        path="/invoices/session/:invoiceId"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({ id: "HEADER.INVOICES" }),
                pathname: "/invoices",
              },
              {
                title: formatMessage({ id: "BREADCRUMBS.INVOICE.DETAILS" }),
                pathname: "/invoices/session/:invoiceId",
              },
            ]}
            requiredPermissions={["read:invoices"]}
          >
            <InvoiceDetailsSession />
          </PrivateRoute>
        }
      />

      <Route
        path="/map"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-admin",
              "portControl-standard",
            ]}
          >
            <AdvancedMapPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/map/m/supply-pits"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            layout={MapMobileLayout}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-admin",
              "portControl-standard",
            ]}
          >
            <MapMobileSupplyPitsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/map/m/shore-power-units/:id"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            layout={MapMobileLayout}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-admin",
              "portControl-standard",
            ]}
          >
            <MapMobileSpuDetails />
          </PrivateRoute>
        }
      />

      <Route
        path="/map/m/supply-pits/:supplyPitId"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            layout={MapMobileLayout}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-admin",
              "portControl-standard",
            ]}
          >
            <MapMobileSupplyPitsDetailsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/map/m/out-of-service"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            layout={MapMobileLayout}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-admin",
              "portControl-standard",
            ]}
          >
            <MapMobileOutOfServicePage />
          </PrivateRoute>
        }
      />

      <Route
        path="/map/m/shore-power-units"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            layout={MapMobileLayout}
            requiredRole={[
              "system-admin",
              "system-standard",
              "portOwner-admin",
              "portOwner-standard",
              "portService-admin",
              "portService-standard",
              "portControl-admin",
              "portControl-standard",
            ]}
          >
            <MapMobileShorePowerUnitsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/map/m"
        element={<Navigate to="/map/m/supply-pits" replace />}
      />

      <Route
        path="/map/supply-pit/:supplyPitId"
        element={
          <PrivateRoute>
            <AdvancedMapPage />
          </PrivateRoute>
        }
      />
      <Route
        path="/user/profile"
        element={
          <PrivateRoute showBreadcrumbs={false}>
            <UserProfilePage />
          </PrivateRoute>
        }
      />
      <Route
        path="/user/notifications"
        element={
          <PrivateRoute
            showBreadcrumbs={false}
            requiredPermissions={["read:notifications"]}
          >
            <UserNotificationsPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/signal-testing"
        element={
          <PrivateRoute showBreadcrumbs={false} requiredRole={["system-admin"]}>
            <SignalTesting />
          </PrivateRoute>
        }
      />

      <Route
        path="/roles-and-permissions"
        element={
          <PrivateRoute showBreadcrumbs={false} requiredRole={["system-admin"]}>
            <AuthorisationDetails />
          </PrivateRoute>
        }
      />

      <Route
        path="/todo"
        element={
          <PrivateRoute
            breadcrumbs={[
              {
                title: formatMessage({
                  defaultMessage: "Todo",
                  description: "Topnav / Breadcrumb",
                }),
                pathname: "/todo",
              },
            ]}
          >
            <TodoPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/"
        element={
          <PrivateRoute>
            <UserRoleBasedRouter />
          </PrivateRoute>
        }
      />
    </Routes>
  );
};
